import type { SortOption } from '@/components/toolbar';
import type { CollectionSortOption } from '@/components/collection/CollectionSortButton';
import type { UserItem } from '@/types/userItem';

export type StatusBucket = 'listed' | 'private' | 'priced' | 'taking_offers' | 'sold';

/** Threshold for "high-value" visual accents. Matches the duty-of-care $500 nudge. */
export const HIGH_VALUE_THRESHOLD_CENTS = 50_000;

/** Ms threshold for a listed item's price being considered "stale". */
const STALE_MS = 30 * 24 * 60 * 60 * 1000;

export const matchesStatusBucket = (
  item: { visibility?: string; asking_price_cents?: number | null; is_negotiable?: boolean | null; sold_at?: string | null },
  bucket: StatusBucket,
): boolean => {
  if (bucket === 'sold') return !!item.sold_at;
  if (bucket === 'listed') return !item.sold_at && item.visibility === 'listed';
  if (bucket === 'private') return !item.sold_at && item.visibility === 'private';
  if (bucket === 'priced') return item.visibility === 'listed' && !item.sold_at && (item.asking_price_cents ?? 0) > 0;
  if (bucket === 'taking_offers') return item.visibility === 'listed' && !item.sold_at && !!item.is_negotiable;
  return false;
};

export const isSoldItem = (item: { sold_at?: string | null }): boolean => !!item.sold_at;

export const isActiveInventoryItem = (item: { sold_at?: string | null }): boolean => !item.sold_at;

// ─── Row / card predicates ────────────────────────────────────────────────
export const isStockPhotoItem = (item: UserItem): boolean =>
  !item.images?.[0]?.image_url && !!item.stock_image_url;

export const hasNoRealPhoto = (item: UserItem): boolean =>
  !item.images?.[0]?.image_url;

export const isHighValue = (item: UserItem): boolean =>
  (item.asking_price_cents ?? 0) >= HIGH_VALUE_THRESHOLD_CENTS;

// ─── Inventory Shortcut presets ───────────────────────────────────────────
export type InventoryShortcutId =
  | 'all'
  | 'sold'
  | 'pending_sales'
  | 'unlisted'
  | 'missing_photos'
  | 'stale_prices'
  | 'priced_no_offers';

export interface InventoryShortcut {
  id: InventoryShortcutId;
  label: string;
  /** Return true to keep the item in view. */
  predicate: (item: UserItem) => boolean;
}

const isStale = (item: UserItem): boolean => {
  const t = item.updated_at ? Date.parse(item.updated_at) : NaN;
  if (Number.isNaN(t)) return false;
  return Date.now() - t > STALE_MS;
};

export const isPendingSale = (item: UserItem): boolean =>
  !!(item as any).pending_sale_at && !item.sold_at;

export const INVENTORY_SHORTCUTS: InventoryShortcut[] = [
  { id: 'all', label: 'All', predicate: isActiveInventoryItem },
  {
    id: 'sold',
    label: 'Sold',
    predicate: isSoldItem,
  },
  {
    id: 'pending_sales',
    label: 'Pending Sales',
    predicate: isPendingSale,
  },
  {
    id: 'unlisted',
    label: 'Unlisted',
    predicate: (i) => !i.sold_at && i.visibility !== 'listed' && !isPendingSale(i),
  },
  {
    id: 'missing_photos',
    label: 'Missing photos',
    predicate: (i) => !i.sold_at && hasNoRealPhoto(i) && !isPendingSale(i),
  },
  {
    id: 'stale_prices',
    label: 'Stale prices',
    predicate: (i) => !i.sold_at && i.visibility === 'listed' && (i.asking_price_cents ?? 0) > 0 && isStale(i),
  },
  {
    id: 'priced_no_offers',
    label: 'Priced, no offers',
    predicate: (i) => !i.sold_at && i.visibility === 'listed' && (i.asking_price_cents ?? 0) > 0 && !i.is_negotiable,
  },
];

export const INVENTORY_SORT_OPTIONS: SortOption<CollectionSortOption>[] = [
  { value: 'priced_first', label: 'Priced First' },
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'high_quantity', label: 'High Quantity First' },
  { value: 'no_images', label: 'No Images First' },
  { value: 'price_asc', label: 'Price: Low to High' },
  { value: 'price_desc', label: 'Price: High to Low' },
  { value: 'title_asc', label: 'Title: A to Z' },
];
